/**
 * Czech-format, Europe/Prague-anchored date & time helpers.
 *
 * The whole app speaks one date dialect: `dd.mm.yyyy`, 24-hour clock,
 * and — where an instant is involved — the Europe/Prague timezone.
 *
 * Two string shapes flow through the custom pickers, deliberately the
 * same shapes the native `<input type="date|datetime-local">` used so
 * callsites swap the control without reworking their state:
 *   - a *wall date*     — `YYYY-MM-DD`           (no timezone)
 *   - a *wall datetime* — `YYYY-MM-DDTHH:mm`     (Prague wall-clock)
 *
 * A wall datetime only becomes an absolute instant when it is paired
 * with a zone — `pragueWallToIso` does that, `isoToPragueWall` is the
 * inverse. Wall dates carry no zone and need no conversion.
 */

export const PRAGUE_TZ = "Europe/Prague";

const CZ_DOW = ["po", "út", "st", "čt", "pá", "so", "ne"];

/** Day-of-week header labels, Monday-first (Czech convention). */
export function czWeekdayLabels(): readonly string[] {
  return CZ_DOW;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Wall-clock components of an absolute instant, read in Prague. */
function partsInPrague(d: Date): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: PRAGUE_TZ,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const m: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) m[p.type] = p.value;
  return {
    year: Number(m.year),
    month: Number(m.month),
    day: Number(m.day),
    hour: Number(m.hour),
    minute: Number(m.minute),
    second: Number(m.second),
  };
}

/** Prague's UTC offset (ms) at the given instant — DST-aware. */
function pragueOffsetMs(d: Date): number {
  const p = partsInPrague(d);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - d.getTime();
}

/**
 * Interpret a `YYYY-MM-DDTHH:mm` wall datetime as Prague local time
 * and return the matching absolute instant as a UTC ISO string.
 * Returns `""` for empty/invalid input.
 */
export function pragueWallToIso(wall: string): string {
  const m = wall.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return "";
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);
  // One Newton step is enough away from the 1-hour DST seam: guess the
  // instant as if the wall time were UTC, read Prague's offset there,
  // then correct by it.
  const utcGuess = Date.UTC(y, mo - 1, da, h, mi);
  const offset = pragueOffsetMs(new Date(utcGuess));
  return new Date(utcGuess - offset).toISOString();
}

/**
 * Inverse of `pragueWallToIso`: render an absolute ISO instant as the
 * `YYYY-MM-DDTHH:mm` Prague wall datetime a picker can edit.
 */
export function isoToPragueWall(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = partsInPrague(d);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** The Prague calendar date (`YYYY-MM-DD`) an ISO instant falls on. */
export function isoToPragueDate(iso: string): string {
  return isoToPragueWall(iso).slice(0, 10);
}

// --- CZ display formatting --------------------------------------------------

/** `YYYY-MM-DD` → `dd.mm.yyyy`. Empty/invalid input → `""`. */
export function formatCzDate(isoDate: string): string {
  const m = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/** `YYYY-MM-DDTHH:mm` → `dd.mm.yyyy HH:mm`. Empty/invalid → `""`. */
export function formatCzDateTime(wall: string): string {
  const m = wall.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return "";
  return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
}

// --- CZ parsing -------------------------------------------------------------

/** True when `y-m-d` is a real calendar date. */
function isRealDate(y: number, mo: number, da: number): boolean {
  if (mo < 1 || mo > 12 || da < 1 || da > 31) return false;
  const d = new Date(y, mo - 1, da);
  return d.getFullYear() === y && d.getMonth() === mo - 1 && d.getDate() === da;
}

/** `dd.mm.yyyy` (lenient on leading zeros) → `YYYY-MM-DD`, or `null`. */
export function parseCzDate(text: string): string | null {
  const m = text.trim().match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/);
  if (!m) return null;
  const da = Number(m[1]);
  const mo = Number(m[2]);
  const y = Number(m[3]);
  if (!isRealDate(y, mo, da)) return null;
  return `${y}-${pad(mo)}-${pad(da)}`;
}

/** `dd.mm.yyyy HH:mm` → `YYYY-MM-DDTHH:mm`, or `null`. */
export function parseCzDateTime(text: string): string | null {
  const m = text.trim().match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})[ ,]+(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const da = Number(m[1]);
  const mo = Number(m[2]);
  const y = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);
  if (!isRealDate(y, mo, da) || h > 23 || mi > 59) return null;
  return `${y}-${pad(mo)}-${pad(da)}T${pad(h)}:${pad(mi)}`;
}

/**
 * Lenient parser for the `DateTimePicker` input — accepts both
 * `dd.mm.yyyy HH:mm` and bare `dd.mm.yyyy`. Date-only input defaults
 * to `00:00`, which is the sensible boundary for filters that mean
 * "since this day" / "up to and including this day's start".
 */
export function parseCzDateOrDateTime(text: string): string | null {
  const full = parseCzDateTime(text);
  if (full) return full;
  const dateOnly = parseCzDate(text);
  if (dateOnly) return `${dateOnly}T00:00`;
  return null;
}

// --- Calendar grid helpers --------------------------------------------------

/** Czech month + year heading for a calendar view, e.g. `květen 2026`. */
export function czMonthLabel(year: number, monthIndex: number): string {
  return new Intl.DateTimeFormat("cs-CZ", { month: "long", year: "numeric" }).format(
    new Date(year, monthIndex, 1),
  );
}

/**
 * The 42 days (6 weeks, Monday-first) that fill a month's calendar
 * grid — including the trailing days of the previous month and the
 * leading days of the next so every week is complete.
 */
export function monthGridDays(year: number, monthIndex: number): Date[] {
  const first = new Date(year, monthIndex, 1);
  // JS getDay(): 0=Sun … 6=Sat. Monday-first offset.
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(year, monthIndex, 1 - lead);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

/** `Date` → `YYYY-MM-DD` using its local calendar fields. */
export function dateToWallDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Today's Prague calendar date as `YYYY-MM-DD`. */
export function todayPragueDate(): string {
  return isoToPragueDate(new Date().toISOString());
}

/**
 * The absolute instant Prague's current day began — the line between time
 * that can be confirmed and time that is still being worked.
 *
 * Prague rather than the browser's zone so a laptop travelling with someone
 * doesn't move the working day, and so the answer matches the server's
 * (`server/lib/workday.ts`), which is where the rule is actually enforced.
 */
export function startOfTodayPrague(): Date {
  return new Date(pragueWallToIso(`${todayPragueDate()}T00:00`));
}
